import type { EventParticipantRole } from 'models';
import { useCallback } from 'react';
import { Col, Form, Row } from 'ui';
import type {
  InvitationSearchEmailOptionLabel,
  InvitationSearchOptionLabel,
  InvitationSearchUserOptionLabel,
} from '../types';
import { CommunityInvitationsList } from './community-invitations-list';
import { IndividualInvitationsList } from './individual-invitations-list';

type IndividualInvitation = InvitationSearchEmailOptionLabel | InvitationSearchUserOptionLabel;

const isIndividualInvitation = (invitation: InvitationSearchOptionLabel): invitation is IndividualInvitation =>
  invitation.type === 'user' || invitation.type === 'email';

const isUserInvitation = (invitation: InvitationSearchOptionLabel): invitation is InvitationSearchUserOptionLabel =>
  invitation.type === 'user';

type InvitationsListFormValues = {
  invitations?: InvitationSearchOptionLabel[];
};

type P = {
  invitations: InvitationSearchOptionLabel[];
  showRoleSelect?: boolean;
};

const InvitationsList = ({ invitations, showRoleSelect = true }: P) => {
  const form = Form.useFormInstance<InvitationsListFormValues>();

  const handleRemove = useCallback(
    (value: string) => {
      form.setFieldValue(
        'invitations',
        invitations.filter((invitation) => invitation.value !== value),
      );
    },
    [form, invitations],
  );

  const handleRoleChange = useCallback(
    (value: string, role: EventParticipantRole) => {
      form.setFieldValue(
        'invitations',
        invitations.map((invitation) => (invitation.value === value ? { ...invitation, role } : invitation)),
      );
    },
    [form, invitations],
  );

  if (invitations.length === 0) {
    return null;
  }

  const communityInvitations = invitations.filter((invitation) => invitation.type === 'community');
  const individualInvitations: IndividualInvitation[] = showRoleSelect
    ? invitations.filter((invitation) => isIndividualInvitation(invitation))
    : invitations.filter((invitation) => isUserInvitation(invitation));

  return (
    <Row className="invitations-list" gutter={[0, 'ms']}>
      {communityInvitations.length > 0 ? (
        <Col span={24}>
          <CommunityInvitationsList invitations={communityInvitations} onRemove={handleRemove} />
        </Col>
      ) : null}
      {individualInvitations.length > 0 ? (
        <Col span={24}>
          <IndividualInvitationsList
            invitations={individualInvitations}
            onRemove={handleRemove}
            onRoleChange={showRoleSelect ? handleRoleChange : undefined}
          />
        </Col>
      ) : null}
    </Row>
  );
};

export default InvitationsList;
